import type React from 'react';
import { useTranslation } from 'react-i18next';

type TermsCheckboxProps = {
  checked: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: string;
  supportError?: boolean;
};

export const TermsCheckbox = ({
  checked,
  onChange,
  error,
  supportError = false,
}: TermsCheckboxProps) => {
  const { t } = useTranslation();
  // Handle link clicks without triggering checkbox change
  const handleLinkClick = (e: React.MouseEvent, url: string) => {
    e.preventDefault();
    e.stopPropagation();
    // Open link in new tab
    window.open(url, '_blank');
  };

  // Handle checkbox container click
  const handleContainerClick = (e: React.MouseEvent) => {
    // Only trigger checkbox if not clicking on a link
    const target = e.target as HTMLElement;
    if (!target.closest('a') && !target.closest('[data-link]')) {
      const checkbox = document.getElementById('agree-terms') as HTMLInputElement;
      if (checkbox) {
        checkbox.click();
      }
    }
  };

  return (
    <div className="flex flex-col">
      <div className="flex cursor-pointer items-center gap-0" onClick={handleContainerClick}>
        {/* Custom Checkbox - 按照Figma设计的24x32px尺寸 */}
        <div className="flex h-[32px] w-[24px] items-center justify-start">
          <div className="relative">
            <input
              type="checkbox"
              id="agree-terms"
              checked={checked}
              onChange={onChange}
              className="peer sr-only"
            />
            {/* Checkbox外观 - 根据Figma设计调整 */}
            <div className="flex size-[16px] items-center justify-center rounded-[4px] border border-[#C5C5C5] bg-white transition-all duration-200 peer-checked:border-[#326BFB] peer-checked:bg-[#326BFB] peer-focus:ring-2 peer-focus:ring-[#326BFB]/20">
              {checked && (
                <svg
                  width="10"
                  height="8"
                  viewBox="0 0 10 8"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="text-white"
                >
                  <path
                    d="M1 4L3.5 6.5L9 1"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </div>
          </div>
        </div>

        {/* Label - 按照设计稿的字体样式 */}
        <div className="text-[12px] font-normal leading-[18px] text-[#252626]">
          {t('components.termsCheckbox.agreement')}{' '}
          <a
            href="/policies/terms-of-use"
            target="_blank"
            data-link="terms"
            onClick={(e) => handleLinkClick(e, '/policies/terms-of-use')}
            className="auth-link cursor-pointer text-[#252626] hover:text-[#2557d4]"
            rel="noreferrer"
          >
            {t('components.termsCheckbox.termsOfService')}
          </a>{' '}
          {t('components.termsCheckbox.and')}{' '}
          <a
            href="/policies/privacy-policy"
            target="_blank"
            data-link="privacy"
            onClick={(e) => handleLinkClick(e, '/policies/privacy-policy')}
            className="auth-link cursor-pointer text-[#252626] hover:text-[#2557d4]"
            rel="noreferrer"
          >
            {t('components.termsCheckbox.privacyPolicy')}
          </a>
        </div>
      </div>

      {/* Error message with reserved space */}
      {supportError && (
        <div className="h-[18px]">
          {error && (
            <p className="text-[12px] font-normal leading-[18px] text-[#FA4E3E]">{error}</p>
          )}
        </div>
      )}
    </div>
  );
};